import useStore from "../helpers/useStore";
import { UserContext } from "../stores/User/User.provider";

/**
 * Does the logged-in user hold the "chief" role?
 *
 * Use this for surfaces that are chief-only regardless of the debug toggle.
 * If the surface should additionally require the Debug UI toggle, use
 * `useChiefDebugAccess` below instead.
 *
 * Components using this hook must be wrapped in `observer` (mobx) so they
 * re-render when the user changes.
 */
export const useIsChief = (): boolean => {
  const UserStore = useStore(UserContext);
  const { user } = UserStore;

  return Boolean(
    user?.roles?.some(
      (role: { id?: string; name?: string }) =>
        role?.id === "chief" || String(role?.name).toLowerCase() === "chief",
    ),
  );
};

/**
 * Gate for experimental / maintenance-grade surfaces.
 *
 * A surface guarded by this hook is only revealed when BOTH hold:
 *  1. the logged-in user has the "chief" role, and
 *  2. the debug UI is enabled (Debug UI toggle in the user profile menu).
 *
 * This is the same criteria that reveals the maintenance module tab in the
 * admin area and the MobiliCalendar preview. Reuse it for any future
 * "chief-only while debugging" feature so the rule stays in one place.
 *
 * Components using this hook must be wrapped in `observer` (mobx) so they
 * re-render when `UserStore.debugUI` or the user changes.
 */
export const useChiefDebugAccess = (): boolean => {
  const UserStore = useStore(UserContext);
  const isChief = useIsChief(); // called unconditionally — it is a hook

  return Boolean(UserStore.debugUI) && isChief;
};

export default useChiefDebugAccess;
